import { ViewerEventType, ViewerViewportChangeEvent, ViewerLayoutZoomChangeEvent } from '../../types/ViewerEvents.type';
import { ASSET_TYPE } from '../../constants/Constants';
import { browser } from '../../helpers/BrowserHelper';
import { FullscreenHelper } from '../../helpers/FullscreenHelper';
import { OpenInOfficeOnlineHelper } from '../../helpers/OpenInOfficeOnlineHelper';
import { getViewState, watchScroll } from '../../utils/ui-utils';
import { IframePresentationRenderer } from '../renderers/IframePresentationRenderer';
import { OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT, ObservableDimensionsIframePresentationRenderer } from '../renderers/ObservableDimensionsIframePresentationRenderer';
import { PdfJsPresentationRenderer } from '../renderers/PdfJsPresentationRenderer';
import { AssetViewerView } from '../views/AssetViewerView';
import { ViewerLayout } from './ViewerLayout';
const OFFICE_ONLINE_BAR_DEFAULT_HEIGHT = 40;
export class LayoutVertical extends ViewerLayout {
    constructor(config, eventBus, assetViewerItems, tokenService, pdfJsLinkService) {
        super(config, eventBus, assetViewerItems);
        this.config = config;
        this.eventBus = eventBus;
        this.assetViewerItems = assetViewerItems;
        this.tokenService = tokenService;
        this.pdfJsLinkService = pdfJsLinkService;
        this.isZoomed = false;
        this.currentScale = 1;
        this.container = null;
        this.views = this.createAssetViewerViewsFromAssetViewerItems(this.assetViewerItems);
        this.onFullscreenChange = this.onFullscreenChange.bind(this);
        this.onObservableDimensionsIframeDimensionsChanged = this.onObservableDimensionsIframeDimensionsChanged.bind(this);
        this.addEventListeners();
    }
    set currentView(view) {
        // requestAnimationFrame(() =>
        this.scrollIntoView(view);
        // );
    }
    render(container) {
        this.container = container;
        this.container.classList.add('viewer-app-container');
        this.viewportWidth = this.container.clientWidth;
        this.viewportHeight = this.container.clientHeight;
        this.container.style.overflow = 'auto';
        // if (!browser.ie) {
        //     // Promote container to new layer for improved scrolling performance (IE doesn"t like this)
        //     this.container.style.transform = "translateZ(0)";
        // }
        this.scrollContainer = this.container;
        this.scroll = watchScroll(this.scrollContainer, this.onScroll.bind(this));
        this.calculateLayout();
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
        this.renderAll();
    }
    renderAll() {
        const fragment = document.createDocumentFragment();
        this.views.forEach(view => {
            fragment.appendChild(this.renderView(view));
        });
        // SP-13479 If innerHTML is emptied before the fragment is created child elements in ie11 are lost
        this.container.innerHTML = '';
        this.container.appendChild(fragment);
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this._viewState }));
    }
    renderView(view) {
        const container = view.container;
        const contentWrapper = view.contentWrapper;
        if (OpenInOfficeOnlineHelper.shouldShowPresentInOfficeOnlineBar(view)) {
            contentWrapper.style.position = 'relative';
            contentWrapper.appendChild(view.presentInOfficeOnlineBar);
        }
        const contentElement = view.contentElement;
        if (view.borderBottom) {
            view.container.style.borderBottomWidth = view.borderBottom + 'px';
        }
        this.updateView(view);
        contentWrapper.appendChild(contentElement);
        container.appendChild(contentWrapper);
        return container;
    }
    updateView(view) {
        const container = view.container;
        const contentWrapper = view.contentWrapper;
        const contentElement = view.contentElement;
        contentWrapper.style.paddingTop = view.paddingTop + 'px';
        contentWrapper.style.paddingBottom = view.paddingBottom + 'px';
        contentWrapper.style.paddingLeft = view.paddingLeft + 'px';
        contentWrapper.style.paddingRight = view.paddingRight + 'px';
        contentElement.style.width = view.slideWidth + 'px';
        contentElement.style.height = view.slideHeight + 'px';
        if (view.isZoomed) {
            container.scrollLeft = view.scrollXOffset;
        }
    }
    update(index = null, adjustScrollPosition = true, viewportWidthChanged = false) {
        if (index === -1) {
            console.error('update called with view index -1');
            index = null;
        }
        this.calculateLayout(index, adjustScrollPosition, null, viewportWidthChanged);
        if (index !== null) {
            const view = this.views[index];
            this.updateView(view);
            if (this.scrollYOffset) {
                this.scrollContainer.scrollTop = this.viewState.top + this.scrollYOffset;
            }
        }
        else {
            this.updateAll();
        }
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
    }
    updateAll() {
        this.views.forEach(view => this.updateView(view));
    }
    updateAndKeepScrollPosition() {
        const previousScrollHeight = this.scrollContainer.scrollHeight;
        this.update(null, true, true);
        const nextScrollHeight = this.scrollContainer.scrollHeight;
        const scale = nextScrollHeight / previousScrollHeight;
        this.scrollContainer.scrollTop = this.scroll.lastY * scale;
        // TODO clean up, view separator line doesn"t scale with the views
        this.scrollContainer.scrollLeft = this.isZoomed ? (this.views[0].width - this.viewportWidth) / 2 : 0;
    }
    addViews(view, numViews, suggestedWidth, suggestedHeight) {
        const index = this.views.indexOf(view);
        if (index === -1) {
            // TODO why does this happen?
            console.error('view not found in addViews');
            return;
        }
        const viewsToAdd = [];
        const fragment = document.createDocumentFragment();
        for (let i = 0; i < numViews; i++) {
            const page = i + 1;
            const viewToAdd = this.createAssetViewerViewFromAssetViewerItem(view.assetViewerItem, page);
            viewToAdd.groupId = viewToAdd.assetViewerItem.id;
            if (i === 0) {
                viewToAdd.isFirstOfGroup = true;
            }
            if (i === viewToAdd.assetViewerItem.numViews - 1) {
                viewToAdd.isLastOfGroup = true;
            }
            viewToAdd.contentElement.style.width = suggestedWidth + 'px';
            viewToAdd.contentElement.style.height = suggestedHeight + 'px';
            viewsToAdd.push(viewToAdd);
        }
        this.views = [...this.views.slice(0, index), ...viewsToAdd, ...this.views.slice(index + 1)];
        if (!view.container.parentNode) {
            console.error('no parent', view.container);
        }
        this.calculateLayout(index + numViews, true, numViews);
        if (this.scrollYOffset) {
            this.scrollContainer.scrollTop = this.viewState.top + this.scrollYOffset;
        }
        viewsToAdd.forEach(view => fragment.appendChild(this.renderView(view)));
        if (view.container.parentNode) {
            view.container.parentNode.insertBefore(fragment, view.container.nextSibling);
            view.container.parentNode.removeChild(view.container);
        }
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
        return viewsToAdd;
    }
    // the top and bottom should only be used inside Layout
    calculateLayout(viewToKeepPositionIndex = null, adjustScrollPosition = true, viewsAdded = null, viewportWidthChanged = false) {
        let sumOfViewHeights = 0;
        this.scrollYOffset = null;
        this.views = this.views.map((view, index) => {
            const isLastSlide = index === this.views.length - 1;
            const borderBottom = isLastSlide || (view.groupId && !view.isLastOfGroup) ? 0 : this.config.VIEW_BORDER_BOTTOM;
            let paddingTop;
            if (view.groupId === null || (view.groupId && view.isFirstOfGroup)) {
                if (OpenInOfficeOnlineHelper.shouldShowPresentInOfficeOnlineBar(view)) {
                    const barHeight = view.presentInOfficeOnlineBar.clientHeight || OFFICE_ONLINE_BAR_DEFAULT_HEIGHT;
                    paddingTop = this.config.VIEW_PADDING_TOP + barHeight;
                }
                else {
                    paddingTop = this.config.VIEW_PADDING_TOP;
                }
            }
            else {
                paddingTop = this.config.PAGE_PADDING_TOP;
            }
            const paddingBottom = view.groupId === null || (view.groupId && view.isLastOfGroup)
                ? this.config.VIEW_PADDING_BOTTOM
                : this.config.PAGE_PADDING_BOTTOM;
            const paddingLeft = this.config.VIEW_PADDING_LEFT;
            const paddingRight = this.config.VIEW_PADDING_RIGHT;
            const totalBorderWidth = this.config.VIEW_BORDER * 2;
            const previousViewState = Object.assign({}, view);
            // Default to the smallest document width, (scaling up looks better than scaling down)
            const maxContentWidth = view.assetViewerItem.type === ASSET_TYPE.DOCUMENT
                ? Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH, this.config.MAX_CONTENT_WIDTH_PORTRAIT_DOCUMENTS)
                : Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH);
            const maxContentHeight = this.viewportHeight - (this.config.VIEW_PADDING_TOP + this.config.VIEW_PADDING_BOTTOM + totalBorderWidth);
            if (view.dimensions) {
                const isLandscape = view.dimensions.width / view.dimensions.height > 1;
                const maxContentWidthConfigValue = view.assetViewerItem.type === ASSET_TYPE.DOCUMENT && !isLandscape
                    ? this.config.MAX_CONTENT_WIDTH_PORTRAIT_DOCUMENTS
                    : this.config.MAX_CONTENT_WIDTH;
                let maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), maxContentWidthConfigValue);
                if (view.assetViewerItem.type === ASSET_TYPE.PAGE && this.config.MAX_CONTENT_WIDTH_PAGES) {
                    maxContentWidth = Math.min(this.viewportWidth - (paddingLeft + paddingRight + totalBorderWidth), this.config.MAX_CONTENT_WIDTH_PAGES);
                }
                const width = view.presentationRenderer instanceof IframePresentationRenderer ? maxContentWidth : view.dimensions.width;
                const height = view.presentationRenderer instanceof IframePresentationRenderer ||
                    (view.presentationRenderer instanceof ObservableDimensionsIframePresentationRenderer &&
                        view.dimensions.height === OBSERVABLE_DIMENSIONS_MIN_IFRAME_HEIGHT)
                    ? maxContentHeight
                    : view.dimensions.height;
                const widthScale = width / maxContentWidth;
                const scaledHeight = height / widthScale;
                if (isLandscape &&
                    this.viewportWidth > this.config.LANDSCAPE_FIT_WIDTH_BREAKPOINT_WIDTH &&
                    this.viewportHeight > this.config.LANDSCAPE_FIT_WIDTH_BREAKPOINT_HEIGHT &&
                    scaledHeight > maxContentHeight) {
                    // don"t scale up small images/videos
                    if ((view.assetViewerItem.type === ASSET_TYPE.PHOTO ||
                        view.assetViewerItem.type === ASSET_TYPE.VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.MEETING_VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.PERSONAL_VIDEO_RECORDING) &&
                        width < maxContentWidth &&
                        height < maxContentHeight &&
                        widthScale > 0) {
                        view.slideWidth = width;
                        view.slideHeight = height;
                    }
                    else {
                        const heightScale = height / maxContentHeight;
                        view.slideWidth = width / heightScale;
                        view.slideHeight = height / heightScale;
                    }
                }
                else {
                    // don"t scale up small images/videos
                    if ((view.assetViewerItem.type === ASSET_TYPE.PHOTO ||
                        view.assetViewerItem.type === ASSET_TYPE.VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.MEETING_VIDEO ||
                        view.assetViewerItem.type === ASSET_TYPE.PERSONAL_VIDEO_RECORDING) &&
                        width < this.viewportWidth &&
                        widthScale > 0) {
                        view.slideWidth = width;
                        view.slideHeight = height;
                    }
                    else {
                        view.slideWidth = width / widthScale;
                        view.slideHeight = height / widthScale;
                    }
                }
                if (view.presentationRenderer instanceof ObservableDimensionsIframePresentationRenderer) {
                    view.slideWidth = maxContentWidth;
                    view.slideHeight = height;
                }
                if (!view.defaultScaledWidth || viewportWidthChanged) {
                    view.defaultScaledWidth = view.slideWidth;
                    view.defaultScaledHeight = view.slideHeight;
                }
                if (view.isZoomed) {
                    view.slideWidth = view.defaultScaledWidth * view.currentScale;
                    if (!(view.presentationRenderer instanceof ObservableDimensionsIframePresentationRenderer)) {
                        view.slideHeight = view.defaultScaledHeight * view.currentScale;
                    }
                }
                view.currentScale = view.slideWidth / width;
            }
            else {
                view.slideWidth = maxContentWidth;
                view.slideHeight = maxContentHeight;
            }
            view.width = view.slideWidth + paddingLeft + paddingRight + totalBorderWidth;
            view.height = view.slideHeight + paddingTop + paddingBottom + borderBottom + totalBorderWidth;
            view.top = sumOfViewHeights;
            view.viewTop = sumOfViewHeights;
            view.bottom = view.top + view.height;
            view.paddingTop = paddingTop;
            view.paddingBottom = paddingBottom;
            view.paddingLeft = paddingLeft;
            view.paddingRight = paddingRight;
            view.borderBottom = borderBottom;
            sumOfViewHeights = view.bottom;
            if (this._viewState) {
                // if the current slide height changed, check if the scrollTop should be set, to avoid jumping of the views
                if (index === viewToKeepPositionIndex && previousViewState && !viewsAdded) {
                    const oldHeight = previousViewState.height;
                    const newHeight = view.height;
                    const viewBottomIsAboveViewportBottom = previousViewState.bottom < this._viewState.bottom;
                    this.scrollYOffset = null;
                    if (viewBottomIsAboveViewportBottom && adjustScrollPosition) {
                        this.scrollYOffset = newHeight - oldHeight;
                    }
                    // if view is zoomed, center the scroll position
                    view.scrollXOffset = view.isZoomed ? (view.width - this.viewportWidth) / 2 : null;
                }
                // Adjust the scollPosition so the position of the bottom of the first visible view stays the same
                const viewTopIsAboveViewportBottom = previousViewState.top < this._viewState.bottom;
                // const slideTopIsBelowViewtop = previousViewState.top > this._viewState.top
                if (viewsAdded && index === viewToKeepPositionIndex && viewTopIsAboveViewportBottom) {
                    const oldTop = previousViewState.top;
                    const newTop = view.top;
                    this.scrollYOffset = newTop - oldTop;
                }
            }
            return view;
        });
        this.layoutHeight = sumOfViewHeights;
    }
    scrollIntoView(view) {
        if (!this.scrollContainer) {
            console.warn('Render ViewerLayout to be able to scrollIntoView');
            // this.render();
            return;
        }
        // TODO remove magic number 3, added to make sure you don't see a border of the previous view when navigating
        this.scrollContainer.scrollTop =
            view.groupId && !view.isFirstOfGroup ? view.top - this.config.PAGE_PADDING_BOTTOM + 3 : view.top + 1;
    }
    addEventListeners() {
        super.addEventListeners();
        // this.eventBus.on(ViewerEventType.Zoom, this.onZoom);
        // this.eventBus.on(ViewerEventType.Swipe, this.onSwipe);
        this.eventBus.on(ViewerEventType.FocusChange, this.onFullscreenChange);
        this.eventBus.on(ViewerEventType.ObservableDimensionsIframeDimensionsChanged, this.onObservableDimensionsIframeDimensionsChanged);
        // this.container.addEventListener("touchstart", event => this.onTouchStart(event), true)
        // this.container.addEventListener("touchmove", event => this.onTouchMove(event), true)
        // this.container.addEventListener("touchend", event => this.onTouchEnd(event), true)
    }
    removeEventListeners() {
        super.removeEventListeners();
        // this.eventBus.off(ViewerEventType.Zoom, this.onZoom);
        // this.eventBus.off(ViewerEventType.Swipe, this.onSwipe);
        this.eventBus.off(ViewerEventType.FocusChange, this.onFullscreenChange);
        this.eventBus.off(ViewerEventType.ObservableDimensionsIframeDimensionsChanged, this.onObservableDimensionsIframeDimensionsChanged);
    }
    onScroll(event, state) {
        this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, state);
        // SPW-1729: Don"t fire the VIEWPORT_CHANGE event when entering fullscreen mode fore webapps, video, audio.
        // Because of a chrome bug the viewport container scrolls to the top and messes up device-events
        if (FullscreenHelper.isFullscreen() &&
            this.fullscreenContainer &&
            FullscreenHelper.getFullscreenElement() !== this.fullscreenContainer) {
            return;
        }
        if (this._previousViewState &&
            this._previousViewState.first.view === this._viewState.first.view &&
            this._previousViewState.last.view === this._viewState.last.view &&
            this._previousViewState.focus.view === this._viewState.focus.view) {
            return;
        }
        this._previousViewState = this._viewState;
        this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this._viewState }));
    }
    resize() {
        if (this.container) {
            this.viewportWidth = this.container.clientWidth;
            this.viewportHeight = this.container.clientHeight;
            this._viewState = getViewState(this.views, this.viewportHeight, this.viewportWidth, this.scroll);
            this.resetZoom();
            this.calculateLayout();
        }
    }
    get canZoomIn() {
        return this.config.canZoom && this.currentScale < this.config.MAX_ZOOM_SCALE;
    }
    get canZoomOut() {
        return this.config.canZoom && this.currentScale > this.config.MIN_ZOOM_SCALE;
    }
    zoomIn() {
        if (this.canZoomIn) {
            this.currentScale = this.currentScale + 0.2;
            this.zoom(this.currentScale);
        }
    }
    zoomOut() {
        if (this.canZoomOut) {
            this.currentScale = this.currentScale - 0.2;
            this.zoom(this.currentScale);
        }
    }
    zoom(scale) {
        if (scale === 1) {
            this.resetZoom();
        }
        else {
            this.views.forEach(view => {
                view.isZoomed = true;
                view.currentScale = scale;
                if (view.presentationRenderer instanceof PdfJsPresentationRenderer) {
                    // reset view so canvas can be rerendered
                    view.reset();
                }
            });
            this.isZoomed = true;
            this.updateAndKeepScrollPosition();
            this.views.forEach(view => view.resizeAnnotations());
            this.eventBus.dispatch(new ViewerLayoutZoomChangeEvent());
        }
    }
    resetZoom() {
        this.isZoomed = false;
        this.currentScale = 1;
        this.views.forEach(view => {
            view.isZoomed = false;
            view.currentScale = null;
            if (view.presentationRenderer instanceof PdfJsPresentationRenderer) {
                // reset view so canvas can be rerendered
                view.reset();
            }
        });
        this.updateAndKeepScrollPosition();
        this.views.forEach(view => view.resizeAnnotations());
        this.eventBus.dispatch(new ViewerLayoutZoomChangeEvent());
    }
    onFullscreenChange(event) {
        // SPW-1729: Fire the VIEWPORT_CHANGE event when exiting fullscreen, because of a chrome bug the viewport container could have scrolled
        if (!FullscreenHelper.isFullscreen()) {
            this.eventBus.dispatch(new ViewerViewportChangeEvent({ viewState: this._viewState }));
        }
    }
    onObservableDimensionsIframeDimensionsChanged(event) {
        this.update();
    }
    createAssetViewerViewFromAssetViewerItem(assetViewerItem, page) {
        const ensuredPage = page || 1;
        const id = assetViewerItem.id + (page ? '-' + parseInt(page) : '');
        const annotations = (assetViewerItem.annotations && assetViewerItem.annotations[ensuredPage]) || null;
        return new AssetViewerView(this.config, this.eventBus, this, assetViewerItem, this.tokenService, this.pdfJsLinkService, id, ensuredPage, annotations);
    }
    createAssetViewerViewsFromAssetViewerItems(assetViewerItems) {
        const views = [];
        assetViewerItems.forEach(assetViewerItem => {
            if (assetViewerItem.pageRanges && assetViewerItem.pageRanges.length) {
                assetViewerItem.pageRanges.forEach((pageRange, rangeIndex) => {
                    for (let i = pageRange.start; i <= pageRange.end; i++) {
                        let view = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, i);
                        view.isFirstOfGroup = rangeIndex === 0 && i === pageRange.start;
                        view.isLastOfGroup = rangeIndex === assetViewerItem.pageRanges.length - 1 && i === pageRange.end;
                        view.groupId = assetViewerItem.id;
                        views.push(view);
                    }
                });
            }
            else {
                for (let i = 0; i < assetViewerItem.numViews; i++) {
                    let view;
                    if (assetViewerItem.numViews > 1) {
                        const page = i + 1;
                        view = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, page);
                        view.groupId = assetViewerItem.id;
                        if (i === 0) {
                            view.isFirstOfGroup = true;
                        }
                        if (i === assetViewerItem.numViews - 1) {
                            view.isLastOfGroup = true;
                        }
                    }
                    else {
                        view = this.createAssetViewerViewFromAssetViewerItem(assetViewerItem, assetViewerItem.page);
                    }
                    views.push(view);
                }
            }
        });
        return views;
    }
    createContentWrapper() {
        const element = document.createElement('div');
        element.style.display = 'flex';
        element.style.justifyContent = 'center';
        element.style.boxSizing = 'content-box';
        element.style.touchAction = 'manipulation';
        element.classList.add('vrow-content-container-wrapper');
        return element;
    }
    createContentContainer(view) {
        const element = document.createElement('div');
        element.classList.add('vrow-content-container');
        element.classList.add(`vrow-content-container-${view.assetViewerItem.type}`);
        element.classList.add('loading');
        // element.style.display = "flex"
        element.style.position = 'relative';
        element.style.borderWidth = this.config.VIEW_BORDER ? this.config.VIEW_BORDER + 'px' : '0px';
        element.style.boxSizing = 'content-box';
        element.style.background = 'white';
        element.style.willChange = 'transform';
        // Fucks up border:
        // element.style.transform = "translateZ(0)"
        if (!browser.ie) {
            element.style.justifyContent = 'center';
            element.style.alignItems = 'center';
        }
        if (this.config.TRANSITIONS_ENABLED) {
            element.style.transition = 'opacity .5s ease-in-out';
        }
        return element;
    }
    createContainer() {
        const element = document.createElement('div');
        element.style.boxSizing = 'border-box';
        element.style.minWidth = '100%';
        // display flex, doesn"t work well when zoomed
        element.style.display = 'inline-block';
        element.style.justifyContent = 'center';
        element.style.textAlign = 'center';
        element.classList.add('vrow');
        return element;
    }
}
//# sourceMappingURL=LayoutVertical.js.map